import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { BrandMark } from "@/components/site/BrandMark";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_e88f9df8-0827-4de8-b395-30b21dfa42d4/artifacts/6qr6550v_IMG_7776.jpg";

const links = [
  { href: "#about", label: "Our Story" },
  { href: "#services", label: "Services" },
  { href: "#portfolio", label: "Portfolio" },
  { href: "#why", label: "Why Us" },
  { href: "#guide", label: "Free Guide" },
  { href: "#testimonials", label: "Voices" },
  { href: "#contact", label: "Contact" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (href) => {
    setOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-[color:var(--nr-bg)]/90 backdrop-blur-xl border-b border-[color:var(--nr-line)] shadow-[0_8px_24px_-18px_rgba(45,36,34,0.25)]"
          : "bg-[color:var(--nr-bg)]/40 backdrop-blur-md border-b border-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 flex items-center justify-between h-24">
        <button
          data-testid="nav-logo"
          onClick={() => scrollTo("#hero")}
          className="flex flex-col items-center gap-1 group"
        >
          <BrandMark className="w-7 h-7 text-[color:var(--nr-accent)] transition-transform duration-500 group-hover:-translate-y-0.5" />
          <span className="font-serif-display text-xl text-[color:var(--nr-ink)] leading-none">
            noah<span className="text-[color:var(--nr-accent)]">+</span>rayn
          </span>
        </button>

        <nav className="hidden lg:flex items-center gap-9">
          {links.map((l) => (
            <button
              key={l.href}
              data-testid={`nav-link-${l.href.replace("#", "")}`}
              onClick={() => scrollTo(l.href)}
              className="link-underline text-sm font-medium text-[color:var(--nr-ink)]/80 hover:text-[color:var(--nr-ink)]"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <button
            data-testid="nav-cta-quote"
            onClick={() => scrollTo("#contact")}
            className="btn-primary rounded-full px-6 py-3 text-sm font-semibold tracking-wide"
          >
            Get Free Quote
          </button>
        </div>

        <button
          data-testid="nav-mobile-toggle"
          className="lg:hidden p-2 rounded-full hover:bg-[color:var(--nr-bg-soft)]"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div
          data-testid="nav-mobile-menu"
          className="lg:hidden bg-[color:var(--nr-bg)]/95 backdrop-blur-xl border-t border-[color:var(--nr-line)]"
        >
          <div className="px-6 py-6 flex flex-col gap-5">
            {links.map((l) => (
              <button
                key={l.href}
                data-testid={`nav-mobile-link-${l.href.replace("#", "")}`}
                onClick={() => scrollTo(l.href)}
                className="text-left text-lg font-serif-display text-[color:var(--nr-ink)]"
              >
                {l.label}
              </button>
            ))}
            <button
              data-testid="nav-mobile-cta"
              onClick={() => scrollTo("#contact")}
              className="btn-primary rounded-full px-6 py-3 text-sm font-semibold w-full mt-3"
            >
              Get Free Quote
            </button>
          </div>
        </div>
      )}
    </header>
  );
};
